import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Body1,
  Card,
  Link,
  Subtitle1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { PEOPLE_RULE_TITLES, PeopleRule, type PeopleIssue, type PeopleSheet } from '@lg/shared';

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  header: { display: 'inline-flex', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  list: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
});

/** Validation messages grouped by rule (spec 15.3), each linking to its row. */
export function IssuesPanel({
  issues,
  onOpenRow,
}: {
  issues: PeopleIssue[];
  onOpenRow: (sheet: PeopleSheet, rowNumber: number) => void;
}) {
  const styles = useStyles();
  const withIssues = PeopleRule.options.filter((r) => issues.some((i) => i.rule === r));
  return (
    <Card className={styles.card}>
      <Subtitle1 as="h2">Checks</Subtitle1>
      <Body1>{PeopleRule.options.length} checks run on every change, against Entra ID and D365 data.</Body1>
      <Accordion
        multiple
        collapsible
        defaultOpenItems={withIssues.filter((r) =>
          issues.some((i) => i.rule === r && i.severity === 'error'),
        )}
      >
        {PeopleRule.options.map((rule) => {
          const list = issues.filter((i) => i.rule === rule);
          const errors = list.filter((i) => i.severity === 'error').length;
          return (
            <AccordionItem key={rule} value={rule} disabled={list.length === 0}>
              <AccordionHeader>
                <span className={styles.header}>
                  <b>{rule}</b> {PEOPLE_RULE_TITLES[rule]}
                  {list.length === 0 ? (
                    <Badge size="small" appearance="tint" color="success">
                      pass
                    </Badge>
                  ) : errors ? (
                    <Badge size="small" color="danger">
                      {errors} error{errors > 1 ? 's' : ''}
                    </Badge>
                  ) : (
                    <Badge size="small" color="warning">
                      {list.length} warning{list.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                </span>
              </AccordionHeader>
              <AccordionPanel>
                <ul className={styles.list}>
                  {list.map((i, n) => (
                    <li key={n}>
                      {i.sheet && i.rowNumber !== null ? (
                        <Link
                          as="button"
                          onClick={() => onOpenRow(i.sheet as PeopleSheet, i.rowNumber as number)}
                        >
                          {i.sheet} row {i.rowNumber}
                        </Link>
                      ) : (
                        <b>{i.sheet ?? 'Import'}</b>
                      )}
                      {': '}
                      {i.message}
                      {i.severity === 'warning' && ' (warning)'}
                    </li>
                  ))}
                </ul>
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </Card>
  );
}
